import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BundleFiles } from "@/components/bundle/BundleFiles";
import { BundleItems } from "@/components/bundle/BundleItems";
import { BundlePrice } from "@/components/bundle/BundlePrice";
import { BundleArtwork, BundleInstagram, BundleListing } from "@/components/bundle/BundleMarketing";
import { api, errMsg } from "@/lib/api";

const WorkbookProgress = ({ job, building }) => {
  if (!job) return null;
  if (job.status === "done") {
    return building ? <div className="px-4 py-3 text-sm bg-[#F3E5AB]/60" data-testid="bundle-workbook-progress">Workbook written — building its files and the bundle PDF…</div> : null;
  }
  return (
    <div className={`px-4 py-3 text-sm ${job.status === "failed" ? "bg-[#9B2C2C]/10 text-[#9B2C2C]" : "bg-[#F3E5AB]/60"}`} data-testid="bundle-workbook-progress">
      {job.status === "failed" ? `The workbook couldn't be written: ${job.error}` : `Writing the workbook — ${job.done || 0} of ${job.total} chapters…`}
    </div>
  );
};

export default function BundleEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [form, setForm] = useState({});
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);

  const onBundle = (d) => {
    setB(d);
    setForm({ title: d.title, subtitle: d.subtitle, description: d.description });
  };
  const load = useCallback(() => api.get(`/bundles/${id}`).then((r) => {
    setB(r.data);
    return r.data;
  }), [id]);

  useEffect(() => {
    api.get(`/bundles/${id}`).then((r) => onBundle(r.data)).catch(() => nav("/bundles"));
    api.get("/products").then((r) => setProducts(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, nav]);

  const wbBuilding = b?.workbook_job && b.workbook_job.status !== "failed" && (b.workbook_job.message === "Building files" || !b.files?.pdf_path);
  const running = b && ([b.store_sync?.status, b.workbook_job?.status, b.art_job?.status].includes("running") || wbBuilding);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);

  const save = async (patch) => {
    try {
      setB((await api.put(`/bundles/${id}`, patch)).data);
      toast.success("Saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const act = async (fn, msg) => {
    setBusy(true);
    try {
      setB((await fn()).data);
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm("Delete this bundle? If it's on softspinepress.com it will be switched to Draft.")) return;
    try {
      await api.delete(`/bundles/${id}`);
      nav("/bundles");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!b) return <div className="p-12 text-[#5C5255]">Loading…</div>;
  return (
    <div className="px-6 md:px-12 py-10 max-w-6xl space-y-6" data-testid="bundle-editor">
      <div className="flex items-center justify-between">
        <Link to="/bundles" className="text-sm inline-flex items-center gap-1 hover:underline" data-testid="bundle-back-link"><ArrowLeft size={14} /> Bundles</Link>
        <Button variant="ghost" className="rounded-none text-[#9B2C2C] gap-2" onClick={remove} data-testid="bundle-delete-btn"><Trash2 size={14} /> Delete</Button>
      </div>
      <div>
        <div className="eyebrow">{b.kind === "series" ? "Complete series bundle" : "Bundle"} · {b.items.length} titles</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-tight" data-testid="bundle-title">{b.title}</h1>
      </div>
      <WorkbookProgress job={b.workbook_job} building={wbBuilding} />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="border border-[#2A2325]/10 bg-[#FFFDF9] p-6 space-y-3" data-testid="bundle-details">
            <div className="eyebrow">Details</div>
            <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Bundle title" className="rounded-none" data-testid="bundle-title-input" />
            <Input value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="Subtitle" className="rounded-none" data-testid="bundle-subtitle-input" />
            <Textarea rows={4} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Store description and back cover text" className="rounded-none bg-white" data-testid="bundle-description-input" />
            <Button variant="outline" className="rounded-none" onClick={() => save(form)} data-testid="bundle-details-save-btn">Save details</Button>
          </div>
          <BundlePrice b={b} onSave={save} />
        </div>
        <div className="space-y-6">
          <BundleItems b={b} products={products} onSave={save} />
          <BundleFiles b={b} busy={busy}
            onBuild={() => act(() => api.post(`/bundles/${id}/build`), "Bundle PDF & ebook built")}
            onPublish={() => act(() => api.post(`/bundles/${id}/publish`))}
            onUnpublish={() => window.confirm("Switch this bundle to Draft on softspinepress.com?") && act(() => api.post(`/bundles/${id}/unpublish`), "Bundle unpublished")} />
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <BundleListing b={b} onBundle={onBundle} />
        <BundleArtwork b={b} onReload={load} />
      </div>
      <BundleInstagram b={b} />
    </div>
  );
}
